'use client'

// Lightweight client-analytics tracker (Phase A). Captures pageviews on every
// route change and clicks on meaningful interactive elements (links, buttons,
// [data-track]), batches them, and flushes to /api/events/ingest on a 10s timer
// and on page-hide (via sendBeacon, so the last batch survives the unload).
// Mounted once in the root layout; persists across client navigations. Must
// never throw into the app — every path is guarded.

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import {
  deriveClickLabel,
  isDuplicatePageview,
  MAX_EVENTS_PER_BATCH,
  type LastPageview,
} from '@/lib/analytics/user-event'
import { getAnonId } from '@/lib/analytics/anon-id'

const FLUSH_MS = 10_000
const INGEST_URL = '/api/events/ingest'

// Module-scoped so it survives a component remount — a remount is one cause of
// the duplicate pageview we're de-duping (the effect fires again for the same
// path). A full page reload re-imports the module and resets this to null, so a
// genuine new visit still records.
let lastPageview: LastPageview = null

type QueuedEvent = { type: 'pageview' | 'click'; path: string; label?: string | null }

export default function ClientAnalytics() {
  const pathname = usePathname()
  const queue = useRef<QueuedEvent[]>([])
  const pathRef = useRef<string>(pathname ?? '/')
  const anonRef = useRef<string | null>(null)

  useEffect(() => {
    anonRef.current = getAnonId()
  }, [])

  function flush(useBeacon = false) {
    if (queue.current.length === 0) return
    const events = queue.current.splice(0, queue.current.length)
    const payload = JSON.stringify({ anonId: anonRef.current, events })
    try {
      if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon(INGEST_URL, new Blob([payload], { type: 'application/json' }))
      } else {
        void fetch(INGEST_URL, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: payload,
        }).catch(() => {})
      }
    } catch {
      // analytics must never break the app
    }
  }

  // Pageview on every route change. Keep pathRef current even when we skip the
  // pageview, so clicks are still attributed to the right path.
  useEffect(() => {
    if (!pathname) return
    pathRef.current = pathname
    const now = Date.now()
    // Drop a same-path double-fire (remount / effect re-invoke) so it doesn't
    // inflate view counts; a real reload/re-visit still records.
    if (isDuplicatePageview(lastPageview, pathname, now)) return
    lastPageview = { path: pathname, ts: now }
    queue.current.push({ type: 'pageview', path: pathname })
    if (queue.current.length >= MAX_EVENTS_PER_BATCH) flush()
  }, [pathname])

  // Click capture + periodic / unload flush. Mounted once.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const start = e.target as Element | null
      const el = start?.closest?.('a, button, [role="button"], [data-track]') ?? null
      if (!el) return
      queue.current.push({ type: 'click', path: pathRef.current, label: deriveClickLabel(el) })
      if (queue.current.length >= MAX_EVENTS_PER_BATCH) flush()
    }
    function onHide() {
      flush(true)
    }
    function onVisibility() {
      if (document.visibilityState === 'hidden') flush(true)
    }
    document.addEventListener('click', onClick, { capture: true })
    window.addEventListener('pagehide', onHide)
    document.addEventListener('visibilitychange', onVisibility)
    const timer = setInterval(() => flush(), FLUSH_MS)
    return () => {
      document.removeEventListener('click', onClick, { capture: true } as EventListenerOptions)
      window.removeEventListener('pagehide', onHide)
      document.removeEventListener('visibilitychange', onVisibility)
      clearInterval(timer)
      flush(true)
    }
  }, [])

  return null
}
